import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { ClubFull } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import AboutTabs from "./components/AboutTabs";

const VALUES = [
  {
    icon: "ri-heart-3-line",
    title: "봉사",
    text: "지역사회의 어려운 이웃과 함께하며 나눔의 가치를 실천합니다.",
  },
  {
    icon: "ri-team-line",
    title: "친교",
    text: "회원 간 신뢰를 바탕으로 서로의 사업과 성장을 응원합니다.",
  },
  {
    icon: "ri-seedling-line",
    title: "미래",
    text: "다음 세대를 위한 봉사의 씨앗을 꾸준히 심어갑니다.",
  },
];

export default function Greeting() {
  const { data, loading, error, reload } = useApi<ClubFull>("club.php");

  const greeting = (data?.greeting ?? "").trim();
  const greetingName = (data?.greeting_name ?? "").trim();

  return (
    <>
      <PageBanner
        title="인사말"
        description="대전민들레로타리클럽은 봉사의 씨앗을 멀리 퍼뜨리는 사람들의 모임입니다."
        breadcrumb={[{ label: "클럽소개", to: "/about" }, { label: "인사말" }]}
      />
      <AboutTabs />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        {loading ? (
          <div className="space-y-5">
            <Skeleton className="h-6 w-28 rounded-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-5 w-40 self-end ml-auto" />
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="인사말을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : greeting ? (
          <>
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-semibold text-secondary-900">
              <i className="ri-chat-quote-line" aria-hidden="true"></i>
              회장 인사말
            </span>
            <div className="mt-6 whitespace-pre-line break-words text-sm md:text-base leading-loose text-foreground-700">
              {greeting}
            </div>
            {greetingName && (
              <p className="mt-6 text-right font-heading font-bold text-foreground-950">
                {greetingName}
              </p>
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-chat-quote-line"
            title="아직 등록된 인사말이 없습니다"
            description="인사말이 등록되면 이곳에 표시됩니다."
          />
        )}

        {/* 클럽이 지향하는 가치 */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="rounded-xl border border-background-200 bg-background-50 p-4"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className={`${v.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 font-heading font-bold text-foreground-950">
                {v.title}
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                {v.text}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}